import React, { useEffect, useCallback, useReducer } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { fromC, toC } from 'altrac-conversions'

import { DecimalInput, isNumber } from '../Inputs/Inputs'
import AppDispatcher from '../../../dispatchers/AppDispatcher'
import withAppStore from '../../../util/withAppStore'

import styles from '../../../../css/settings.module.css'

// Constants
const TEMP_RANGE = { min: -40, max: 60 }
const MIN_TEMP_DIFF = 0.1

// Validation functions
const validateTemperature = (value, tempConv) => {
  if (!isNumber(value)) return null

  const tempInC = toC(Number(value), tempConv, 1)
  if (tempInC <= TEMP_RANGE.max && tempInC > TEMP_RANGE.min) {
    return Math.round(Number(value) * 10) / 10
  }
  return null
}

const validateTemperaturePair = (tempStart, tempStop, tempConv) => {
  const tempStartClean = validateTemperature(tempStart, tempConv)
  const tempStopClean = validateTemperature(tempStop, tempConv)

  // Both empty
  if (tempStart === '' && tempStop === '') {
    return { tempStartInvalid: true, tempStopInvalid: true }
  }

  // One empty
  if (tempStart === '') {
    return { tempStartInvalid: true, tempStopInvalid: tempStopClean === null }
  }
  if (tempStop === '') {
    return { tempStartInvalid: tempStartClean === null, tempStopInvalid: true }
  }

  // Both have values
  if (tempStartClean === null || tempStopClean === null) {
    return {
      tempStartInvalid: tempStartClean === null,
      tempStopInvalid: tempStopClean === null
    }
  }

  // Check if start > stop
  if (tempStartClean > tempStopClean) {
    return { tempStartInvalid: true, tempStopInvalid: true }
  }

  // Check minimum difference
  if ((tempStopClean - tempStartClean) < MIN_TEMP_DIFF) {
    return { tempStartInvalid: true, tempStopInvalid: true }
  }

  return { tempStartInvalid: false, tempStopInvalid: false }
}

// Extract device settings initialization
const initializeDeviceSettings = (devices, tempConv) => {
  if (!devices || devices.length === 0) return { deviceSettings: [], tempStart: '', tempStop: '' }

  const firstDevice = devices[0]
  const { application_settings: appSettings, application_settings_new: appSettingsNew } = firstDevice

  // Get initial values with fallbacks
  let initialTempStart = 0
  let initialTempStop = 1.6667

  const newSettings = appSettingsNew?.settings
  const currentSettings = appSettings?.settings

  if (newSettings?.tempStart && isNumber(newSettings.tempStart) && appSettingsNew?.status === 'new') {
    initialTempStart = newSettings.tempStart
  } else if (currentSettings?.tempStart && isNumber(currentSettings.tempStart)) {
    initialTempStart = currentSettings.tempStart
  }

  if (newSettings?.tempStop && isNumber(newSettings.tempStop) && appSettingsNew?.status === 'new') {
    initialTempStop = newSettings.tempStop
  } else if (currentSettings?.tempStop && isNumber(currentSettings.tempStop)) {
    initialTempStop = currentSettings.tempStop
  }

  // Create device settings array
  const deviceSettings = devices.map(device => ({
    address: device.address,
    status: 'new',
    settings: {
      tempStart: initialTempStart,
      tempStop: initialTempStop,
    },
  }))

  return {
    deviceSettings,
    tempStart: fromC(initialTempStart, tempConv, 1),
    tempStop: fromC(initialTempStop, tempConv, 1)
  }
}

// State reducer for better state management
const tempStateReducer = (state, action) => {
  switch (action.type) {
    case 'INITIALIZE':
      return {
        ...state,
        ...action.payload,
        tempStartCleared: false,
        tempStopCleared: false,
      }
    case 'UPDATE_TEMP':
      return {
        ...state,
        [action.field]: action.value,
        [`${action.field}Cleared`]: true,
      }
    case 'UPDATE_VALIDATION':
      return {
        ...state,
        tempStartInvalid: action.tempStartInvalid,
        tempStopInvalid: action.tempStopInvalid,
      }
    case 'UPDATE_DEVICE_SETTINGS':
      return {
        ...state,
        deviceTempSettings: action.deviceSettings,
      }
    default:
      return state
  }
}

const TempSet = ({ devices, tempConv, setValidity }) => {
  const [state, dispatch] = useReducer(tempStateReducer, {
    tempStart: '',
    tempStop: '',
    deviceTempSettings: [],
    tempStartInvalid: false,
    tempStopInvalid: false,
    tempStartCleared: false,
    tempStopCleared: false,
  })

  // Initialize component
  useEffect(() => {
    const { deviceSettings, tempStart, tempStop } = initializeDeviceSettings(devices, tempConv)
    dispatch({
      type: 'INITIALIZE',
      payload: {
        deviceTempSettings: deviceSettings,
        tempStart,
        tempStop,
      }
    })
  }, [devices, tempConv])

  // Dispatch to AppDispatcher when state changes
  useEffect(() => {
    AppDispatcher.dispatch({
      action: 'change-setting',
      setting: state.deviceTempSettings,
    })
    AppDispatcher.dispatch({
      action: 'invalid-setting',
      invalid: state.tempStartInvalid || state.tempStopInvalid,
    })
  }, [state.deviceTempSettings, state.tempStartInvalid, state.tempStopInvalid])

  // Update parent validity
  useEffect(() => {
    setValidity(!state.tempStartInvalid && !state.tempStopInvalid)
  }, [state.tempStartInvalid, state.tempStopInvalid, setValidity])

  const handleFocus = useCallback((event) => {
    const field = event.target.name
    if (!state[`${field}Cleared`]) {
      dispatch({
        type: 'UPDATE_TEMP',
        field,
        value: '',
      })
    }
  }, [state.tempStartCleared, state.tempStopCleared])

  const handleChange = useCallback((field, value) => {
    // Get current values
    const currentTempStart = field === 'tempStart' ? value : state.tempStart
    const currentTempStop = field === 'tempStop' ? value : state.tempStop

    // Validate
    const { tempStartInvalid, tempStopInvalid } = validateTemperaturePair(
      currentTempStart,
      currentTempStop,
      tempConv
    )

    // Update validation state
    dispatch({
      type: 'UPDATE_VALIDATION',
      tempStartInvalid,
      tempStopInvalid,
    })

    // Update temperature value
    dispatch({
      type: 'UPDATE_TEMP',
      field,
      value: formatDecimalValue(value),
    })

    // Update device settings if valid
    if (!tempStartInvalid && !tempStopInvalid) {
      const tempStartClean = validateTemperature(currentTempStart, tempConv)
      const tempStopClean = validateTemperature(currentTempStop, tempConv)

      if (tempStartClean !== null && tempStopClean !== null) {
        const newDeviceSettings = state.deviceTempSettings.map(device => ({
          ...device,
          settings: {
            tempStart: toC(tempStartClean, tempConv, 2),
            tempStop: toC(tempStopClean, tempConv, 2),
          },
        }))

        dispatch({
          type: 'UPDATE_DEVICE_SETTINGS',
          deviceSettings: newDeviceSettings,
        })
      }
    }
  }, [state.tempStart, state.tempStop, state.deviceTempSettings, tempConv])

  const childChange = useCallback((event) => {
    handleChange(event.target.name, event.target.value)
  }, [handleChange])

  const unit = tempConv.toUpperCase()

  return (
    <div className={`${styles.inputArea} ${styles.paddedInputArea}`}>
      <section>
        <FormattedMessage
          id='setting.tempStart'
          defaultMessage='Start Temperature'
          description='Start Temperature label'
        >
          {(txt) => (
            <h5>
              {txt} (°{unit})
            </h5>
          )}
        </FormattedMessage>
        <div className={`${styles.temperature} ${state.tempStartInvalid ? styles.inputIssue : ''}`}>
          <DecimalInput
            value={state.tempStart}
            childChange={childChange}
            field='tempStart'
            onFocus={handleFocus}
            style={styles.temperatureInput}
          />
        </div>
      </section>

      <section>
        <FormattedMessage
          id='setting.tempStop'
          defaultMessage='Stop Temperature'
          description='Stop Temperature label'
        >
          {(txt) => (
            <h5>
              {txt} (°{unit})
            </h5>
          )}
        </FormattedMessage>
        <div className={`${styles.temperature} ${state.tempStopInvalid ? styles.inputIssue : ''}`}>
          <DecimalInput
            value={state.tempStop}
            childChange={childChange}
            field='tempStop'
            onFocus={handleFocus}
            style={styles.temperatureInput}
          />
        </div>
      </section>
    </div>
  )
}

// Helper function to format decimal values
const formatDecimalValue = (value) => {
  const periodLocation = value.indexOf('.')
  if (periodLocation > -1) {
    if (value.slice(-1) === '.' || isNaN(value.slice(-1))) {
      return value
    }
    return value.substring(0, periodLocation + 2)
  }
  return value
}

TempSet.propTypes = {
  devices: PropTypes.array.isRequired,
  tempConv: PropTypes.string.isRequired,
  setValidity: PropTypes.func.isRequired,
}

export default withAppStore(TempSet)
